export function toNumber(value: number | string | null | undefined, fallback = 0): number {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(num) ? fallback : num;
}

export function toNumberOrNull(
  value: number | string | null | undefined
): number | null | undefined {
  if (value === undefined) return undefined;
  if (value === null || value === '') return null;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(num) ? null : num;
}

// The API sends decimals as strings ("2.000") and the inputs send numbers; null and ''
// both mean empty. Strings are only compared numerically against a number, so the notes
// "12" and "12.0" stay different.
export function sameFieldValue(a: unknown, b: unknown): boolean {
  const empty = (v: unknown) => v === null || v === undefined || v === '';
  if (empty(a) || empty(b)) return empty(a) && empty(b);
  if (typeof a === 'number' || typeof b === 'number') return Number(a) === Number(b);
  return a === b;
}

export const NOT_A_NUMBER_MESSAGE = 'Enter a number, like 7.5';

// Typed text, not API values: one optional sign, digits, at most one "." or ",".
// parseFloat would truncate "7,5" to 7 and "22.5abc" to 22.5, saving the wrong number.
// Returns null for anything that is not a clean finite number (blank included).
const TYPED_NUMBER = /^[-+]?(?:\d+[.,]?\d*|[.,]\d+)$/;

export function parseTypedNumber(text: string): number | null {
  const trimmed = text.trim();
  if (!TYPED_NUMBER.test(trimmed)) return null;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export type TypedAmount = { value: number | null } | { error: string };

// For fields where a farm hand may type the decimal as a comma ("3,5" on a comma keypad):
// a bare type="number" input turns "3,5" into 35. Blank is a deliberate clear (value null).
// A comma followed by exactly three digits ("1,250", "-3,500") could be a thousands group
// or a decimal, so it is refused rather than guessed; integer fields (piece counts) and
// money fields (`thousands`) take it as a thousands group because "350,000" and "12,500"
// are how those are written. The sign does not change which of the two it is.
const GROUPED = /^([-+]?)(\d{1,3})((?:,\d{3})+)$/;
const wholeNumberMessage = (example = '350000') => `Enter a whole number, like ${example}`;

export function parseTypedAmount(
  text: string,
  options: { integer?: boolean; thousands?: boolean; example?: string } = {}
): TypedAmount {
  const trimmed = text.trim();
  if (trimmed === '') return { value: null };
  if (options.integer && /^\d+$/.test(trimmed)) return { value: Number(trimmed) };

  const grouped = GROUPED.exec(trimmed);
  // "0,125" is the decimal 0.125; any other leading zero ("01,250") is neither.
  if (grouped && grouped[2] !== '0') {
    const [, sign, head, groups] = grouped;
    if (head.startsWith('0')) {
      return { error: options.integer ? wholeNumberMessage(options.example) : 'Type it without a leading zero.' };
    }
    const asThousands = Number(`${sign}${head}${groups.replace(/,/g, '')}`);
    if (options.integer) {
      return sign ? { error: wholeNumberMessage(options.example) } : { value: asThousands };
    }
    if (options.thousands) return { value: asThousands };
    if (groups.length === 4) {
      const asDecimal = Number(`${sign}${head}.${groups.slice(1)}`);
      return { error: `Is that ${asDecimal} or ${asThousands}? Type it without a comma.` };
    }
    return { error: 'Type it without a comma.' };
  }

  if (options.integer) return { error: wholeNumberMessage(options.example) };
  const n = parseTypedNumber(trimmed);
  return n === null ? { error: NOT_A_NUMBER_MESSAGE } : { value: n };
}

// One entry per pasted cell, in order. Blank cells stay as '' so they keep their row;
// trailing blanks (spreadsheets append a newline) are dropped.
export function splitPastedColumn(text: string): string[] {
  const cells = text.split(/\r\n|\n|\r|\t/).map((c) => c.trim());
  while (cells.length > 0 && cells[cells.length - 1] === '') cells.pop();
  return cells;
}
